// Composition hook — merges per-domain TanStack hooks into the AppData shape
// the layout chrome + route components consume.

import { useMemo } from 'react';
import { useCurrentUser } from '@/state/data/account';
import {
  useUserRole, useSetUserRole, type UserRole,
} from '@/state/application/useUiStore';
import { usePatients } from '@/state/data/patient';
import { useDashboardView } from '@/state/data/dashboard';
import { getDashboardTaskRoles } from '@/data/roles';
import { BOARD_INCLUDE } from '@/logic/dashboard';

export interface DashboardRow {
  userid: string | number;
  firstname?: string;
  lastname?: string;
  name?: { first?: string; last?: string };
  phone?: { number?: string | null };
  phone_number?: string | null;
  ehr_patient_id?: string | null;
  [key: string]: unknown;
}

export interface DashboardData {
  kpis: unknown;
  columns: unknown;
  rows: DashboardRow[];
  pagination: unknown;
  filters: unknown[];
  includes: string[];
}

export interface AppData {
  userRole: UserRole | null;
  currentUser: unknown;
  allPatients: unknown[];
  dashboardData: DashboardData | null;
  loading: boolean;
  error: string | null;
  loadData: () => void;
  handleRoleChange: (role: string) => void;
}

export function useAppData(): AppData {
  // Do NOT consume useAuth here — it owns the session-extend timer and must be
  // invoked exactly once per session; every useAppData call site would spawn
  // its own parallel timer chain.
  const currentUserQuery = useCurrentUser();
  const userRole = useUserRole();
  const handleRoleChange = useSetUserRole();

  const taskRoles = getDashboardTaskRoles(userRole);
  const patientsQuery = usePatients();
  const dashboardQuery = useDashboardView(userRole, { task_roles: taskRoles ?? undefined, include: BOARD_INCLUDE });

  const raw = useMemo(() => patientsQuery.data ?? [], [patientsQuery.data]);
  const dashView = dashboardQuery.data ?? null;

  const dashboardData = useMemo<DashboardData | null>(() => {
    if (!dashView || (dashView as { status?: string }).status !== 'success') return null;
    const view = dashView as {
      rows: Array<{ userid: string | number; name?: { first?: string; last?: string }; ehr_patient_id?: string | null }>;
      kpis: unknown;
      columns: unknown;
      pagination: unknown;
      filters: unknown[];
      includes?: string[];
    };
    const rosterMap = new Map<string, { firstname?: string; lastname?: string; phone_number?: string | null }>();
    (raw as Array<{ userid: string | number; firstname?: string; lastname?: string; phone_number?: string | null }>)
      .forEach((p) => rosterMap.set(String(p.userid), p));
    const merged = view.rows.map((row) => {
      const roster = rosterMap.get(String(row.userid)) || {};
      const first = row.name?.first || roster.firstname || '';
      const last = row.name?.last || roster.lastname || '';
      return {
        ...row,
        firstname: first,
        lastname: last,
        name: { first, last },
        phone: { number: roster.phone_number || null },
        phone_number: roster.phone_number || null,
        ehr_patient_id: row.ehr_patient_id || null,
      } as DashboardRow;
    });
    return {
      kpis: view.kpis,
      columns: view.columns,
      rows: merged,
      pagination: view.pagination,
      filters: view.filters || [],
      // Without it the board's first screen never shows the roster columns.
      includes: view.includes ?? [],
    };
  }, [raw, dashView]);

  // Roster: only patients that show up in the DB view (matches legacy behavior).
  const allPatients = useMemo<unknown[]>(() => {
    if (dashView && (dashView as { status?: string }).status === 'success') {
      const dbIds = new Set((dashView as { rows: Array<{ userid: string | number }> }).rows.map((r) => String(r.userid)));
      return (raw as Array<{ userid: string | number }>).filter((p) => dbIds.has(String(p.userid)));
    }
    return raw as unknown[];
  }, [raw, dashView]);

  const loading = patientsQuery.isLoading || dashboardQuery.isLoading;
  const error = patientsQuery.error?.message
    ?? dashboardQuery.error?.message
    ?? null;

  // Manual refetch — kept for any caller that wants to force a re-fetch.
  const loadData = () => {
    patientsQuery.refetch();
    dashboardQuery.refetch();
  };

  return {
    userRole,
    currentUser: currentUserQuery.data?.currentUser ?? null,
    allPatients,
    dashboardData,
    loading,
    error,
    loadData,
    handleRoleChange,
  };
}

export type { UserRole };
